import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { embeddingJobService } from "@/services/embeddingJobService";
import { Button } from "@/components/ui/button";

export interface ScopeRagStats {
  authorized: boolean;
  topics_count: number;
  threads_count: number;
  documents_count: number;
  indexed_chunks_count: number;
  embedding_status: "pending" | "ready" | "failed" | null;
}

export function useScopeRagStats(scopeType: "topic" | "space" | null, scopeId: string | null) {
  return useQuery({
    queryKey: ["scope-rag-stats", scopeType, scopeId],
    enabled: Boolean(scopeType && scopeId),
    queryFn: async (): Promise<ScopeRagStats | null> => {
      const { data, error } = await supabase.rpc("scope_rag_stats", {
        p_scope_type: scopeType,
        p_scope_id: scopeId,
      });
      if (error) return null;
      const row = Array.isArray(data) ? data[0] : data;
      return (row as ScopeRagStats | undefined) ?? null;
    },
  });
}

export function ScopeRagSummary({
  scopeType,
  scopeId,
  compact = false,
}: {
  scopeType: "topic" | "space";
  scopeId: string;
  compact?: boolean;
}) {
  const stats = useScopeRagStats(scopeType, scopeId);
  const row = stats.data;
  const status = row?.embedding_status ?? "pending";
  const counts = scopeType === "topic"
    ? `${row?.threads_count ?? 0} threads · ${row?.documents_count ?? 0} documents`
    : `${row?.topics_count ?? 0} topics · ${row?.threads_count ?? 0} threads · ${row?.documents_count ?? 0} documents`;

  return (
    <div className={compact ? "px-2 pb-1 text-[10px] text-muted-foreground" : "text-[10px] text-muted-foreground"}>
      <div>{counts}</div>
      <div className="flex items-center gap-2">
        <span>Embedding {status}</span>
        {status === "failed" && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-5 px-1 text-[10px]"
            onClick={() => {
              void embeddingJobService
                .enqueue([{ job_type: scopeType, target_id: scopeId }])
                .then(() => embeddingJobService.process(1));
            }}
          >
            Retry
          </Button>
        )}
      </div>
    </div>
  );
}
